import { useState, useMemo } from "react";
import {
  TextField,
  Button,
  Alert,
  Stack,
  InputAdornment,
  IconButton,
  LinearProgress,
  Box,
  Typography,
  MenuItem,
} from "@mui/material";
import { BsEye, BsEyeSlash } from "react-icons/bs";
import { registerUser } from "../../api";

function scorePassword(pw) {
  const len = pw.length;
  const classes = [
    /[a-z]/.test(pw),
    /[A-Z]/.test(pw),
    /[0-9]/.test(pw),
    /[^A-Za-z0-9]/.test(pw),
  ].filter(Boolean).length;

  let score = 0;
  if (len >= 8) score += 1;
  if (len >= 12) score += 1;
  if (classes >= 2) score += 1;
  if (classes >= 3) score += 1;

  const map = [
    { label: "Mycket svagt", color: "error", pct: 10 },
    { label: "Svagt", color: "error", pct: 25 },
    { label: "OK", color: "warning", pct: 50 },
    { label: "Bra", color: "info", pct: 75 },
    { label: "Starkt", color: "success", pct: 100 },
  ];
  const tips = [];
  if (len < 12) tips.push("minst 12 tecken");
  if (classes < 3) tips.push("blanda stora/små bokstäver, siffror, symboler");
  return { ...map[score], tips };
}

export default function RegisterForm({ onRegistered }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [gender, setGender] = useState("unspecified");
  const [birthDate, setBirthDate] = useState("");
  const [msg, setMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const strength = useMemo(() => scorePassword(password), [password]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setMsg("");
    setSubmitting(true);

    try {
      await registerUser({
        username,
        password,
        password_confirm: passwordConfirm,
        birth_date: birthDate,
        gender: gender || "unspecified",
      });

      onRegistered?.();
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Något gick fel vid registreringen.";
      setMsg(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack component="form" onSubmit={handleRegister} spacing={2} sx={{ pt: 0.5 }}>
      {msg && (
        <Alert severity="error" variant="filled">
          {msg}
        </Alert>
      )}

      <TextField
        label="Användarnamn"
        name="username"
        placeholder="Användarnamn"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        required
        fullWidth
        size="small"
        autoComplete="username"
      />

      <Box>
        <TextField
          label="Lösenord"
          name="password"
          type={showPwd ? "text" : "password"}
          placeholder="Lösenord"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          fullWidth
          size="small"
          autoComplete="new-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPwd((v) => !v)}
                  edge="end"
                  aria-label={showPwd ? "Göm lösenord" : "Visa lösenord"}
                  size="small"
                  sx={{ color: "accent.main" }}
                >
                  {showPwd ? <BsEyeSlash /> : <BsEye />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {password && (
          <Box sx={{ mt: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Styrka: {strength.label}
              </Typography>
              <Typography
                variant="caption"
                color={
                  strength.color === "error"
                    ? "error.main"
                    : strength.color === "warning"
                      ? "warning.main"
                      : strength.color === "info"
                        ? "info.main"
                        : "success.main"
                }
              >
                {strength.pct}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={strength.pct}
              color={strength.color}
              sx={{ height: 6, borderRadius: 999 }}
            />
            {strength.tips.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                Tips: {strength.tips.join(", ")}.
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <TextField
        label="Bekräfta lösenord"
        name="password_confirm"
        type={showPwd ? "text" : "password"}
        placeholder="Bekräfta lösenord"
        value={passwordConfirm}
        onChange={(e) => setPasswordConfirm(e.target.value)}
        required
        fullWidth
        size="small"
        autoComplete="new-password"
      />

      <Stack direction="row" spacing={2}>
        <TextField
          select
          label="Kön"
          name="gender"
          value={gender}
          onChange={(e) => setGender(e.target.value)}
          fullWidth
          size="small"
        >
          <MenuItem value="unspecified">Vill inte uppge</MenuItem>
          <MenuItem value="female">Kvinna</MenuItem>
          <MenuItem value="male">Man</MenuItem>
        </TextField>

        <TextField
          label="Födelsedatum"
          name="birth_date"
          type="date"
          value={birthDate}
          onChange={(e) => setBirthDate(e.target.value)}
          fullWidth
          size="small"
          required
          InputLabelProps={{ shrink: true }}
        />
      </Stack>

      <Button
        type="submit"
        fullWidth
        disabled={submitting}
        variant="contained"
        sx={{ mt: 1 }}
      >
        {submitting ? "Skapar…" : "Skapa konto"}
      </Button>
    </Stack>
  );
}
